// The guard and the loop the THREE cursor-driven surfaces share when they
// re-resolve a binding behind an asynchronous wait: F2 rename
// (`rename/renameProvider.ts`), "Find All References in Tab"
// (`findReferencesInTabAction.ts`) and Monaco's Peek (`referenceProvider.ts`).
//
// WHY THEY RE-RESOLVE. The cursor resolvers walk the Object-Explorer tree
// synchronously, and every name comparison in that walk asks how the server
// tells `dbo` from `DBO` (`go-to/oeTreeQualification.ts`). On a connection
// nothing has probed yet the walk falls back to case-INSENSITIVE, so in a
// `_CS_` database a typed `B.dbo.users` is satisfied by `B.DBO.users` too. Each
// surface already waits for something (the case policy, or the whole index) and
// re-resolves behind that wait, under the policy the wait learnt.
//
// WHAT CAN MOVE UNDER THE WAIT. Three things, and all of them are read fresh by
// the second walk:
//   - the BUFFER — the user keeps typing, and the cursor position was captured
//     when the gesture fired, so the walk would bind whatever now sits there;
//   - the editor's CONNECTION — a tab switch mid-probe pairs the NEW
//     connection's binding with the connection captured at gesture time (SQL
//     Server's rename ALTER is `EXEC sp_rename '[dbo].[users]'`, which carries
//     no server of its own), and lets a peek read an index that belongs to
//     neither gesture;
//   - the editor's DATABASE — same, one level down.
// There is no safe merge of two contexts, so a surface whose context moved
// produces NOTHING: no preview, no tab, no locations. That is one keystroke
// from a retry; a preview whose Apply renames the neighbour is not.
//
// WHY A REFUSED WAIT IS NOT ONE ANSWER. `'no-lifecycle'` is the only rejection
// reason that PROVES there is no policy to learn — the mock engine, an engine
// the app does not index, a connection that left the store — and no index
// behind it either. The cold binding is then the best answer available, and
// refusing would kill F2 and Shift+F12 on every mock-engine connection. Every
// other reason (`'policy-unknown'`, `'cancelled'`, `'build-failed'`, and an
// untyped rejection that cannot be classified at all) leaves the binding
// UNVERIFIED on a connection that does have a policy — and the surface's own
// later wait may well succeed the second time and act on that cold binding. So
// those abort.
//
// WHY THE LOOP IS BOUNDED. A re-resolution that lands in ANOTHER database
// (`Sales` where the cursor cold-bound `sales`) was made under a policy that
// says nothing about the new database's own collation — on SQL Server object
// and schema names follow the DATABASE's collation, which the first wait never
// probed. So the new database is waited for and the cursor re-resolved again,
// until two consecutive resolutions agree on the database. A tree that keeps
// answering differently would loop forever, so the walk is capped and a gesture
// that never settles is abandoned.

import { isNavIndexUnavailable } from '../connection-lifecycle/navIndexUnavailable';

/** What the context guard reads off the editor bindings. Both getters are
 *  optional because every surface's deps type declares them so. */
export type ContextDeps = {
  readonly getConnectionId?: () => string | null;
  readonly getDatabase?: () => string | null;
};

/** `getVersionId` is optional so the unit tests' light model doubles still
 *  satisfy it — both sides then compare `undefined`, which is the only answer
 *  available for a buffer that cannot say whether it changed. */
export type VersionedModel = { readonly getVersionId?: () => number };

/** Everything the re-resolution READS and the user can MOVE while the wait is
 *  in flight — captured when the gesture fired.
 *
 *  Monaco itself guards a bulk edit the same way (`versionId` on a
 *  `ResourceTextEdit`, rejected with "bad state - model changed in the
 *  meantime"), and the EMPTY `WorkspaceEdit` the rename path returns cannot
 *  itself bump the version: `StandaloneBulkEditService.apply` groups edits by
 *  resource and an empty list reaches no `pushEditOperations` at all (verified
 *  against monaco-editor 0.44). So the guard never trips on our own return
 *  value. */
export type InvocationContext = {
  readonly connectionId: string;
  readonly database: string | null;
  readonly versionId: number | undefined;
};

export function captureContext(
  deps: ContextDeps,
  model: VersionedModel,
  connectionId: string,
): InvocationContext {
  return {
    connectionId,
    database: deps.getDatabase?.() ?? null,
    versionId: model.getVersionId?.(),
  };
}

export function contextHeldStill(
  deps: ContextDeps,
  model: VersionedModel,
  captured: InvocationContext,
): boolean {
  return (
    (deps.getConnectionId?.() ?? null) === captured.connectionId
    && (deps.getDatabase?.() ?? null) === captured.database
    && model.getVersionId?.() === captured.versionId
  );
}

/** How many waits one gesture may pay for. Each extra round is a database the
 *  re-resolution moved to, and each costs at most the two one-row probes the
 *  surface's own wait would have paid anyway. Three is generous for the real
 *  shape (cursor database → the database the policy names, then a confirming
 *  pass) and still terminates. */
export const MAX_POLICY_RESOLUTIONS = 3;

/** The minimum a target has to expose for the loop to know when it settled. */
export type PolicyRoutedTarget = { readonly database: string };

export type PolicyAwareResolution<T extends PolicyRoutedTarget> = {
  /** What the cursor bound BEFORE any policy was known. */
  readonly cursorTarget: T;
  /** The surface's own wait for `target`'s database — `casePolicyReadyFor` for
   *  rename and the References tab, `indexReadyFor` for Peek. */
  readonly awaitPolicy: (target: T) => Promise<void>;
  /** Walk the cursor again, under whatever policy the wait just learnt. */
  readonly resolve: () => T | null;
  /** Has the invocation context survived? Checked after EVERY wait. */
  readonly heldStill: () => boolean;
};

export type PolicyAwareResolutionResult<T extends PolicyRoutedTarget> =
  /** The policy was learnt and two resolutions agree on the database. */
  | { readonly kind: 'resolved'; readonly target: T }
  /** `'no-lifecycle'`: there is no policy to learn, so the cold binding stands
   *  and the surface falls back to whatever it does with no index. */
  | { readonly kind: 'unlearnable'; readonly target: T }
  /** Produce nothing for this gesture: the context moved, the wait refused for
   *  a reason that leaves the binding unverified, the learnt policy ruled the
   *  typed spelling out, or the database never settled. */
  | { readonly kind: 'abort' };

export async function resolveBehindPolicyWait<T extends PolicyRoutedTarget>(
  spec: PolicyAwareResolution<T>,
): Promise<PolicyAwareResolutionResult<T>> {
  let target = spec.cursorTarget;
  for (let round = 0; round < MAX_POLICY_RESOLUTIONS; round += 1) {
    const waited = await spec.awaitPolicy(target).then(
      () => 'learnt' as const,
      (err: unknown) =>
        isNavIndexUnavailable(err) && err.reason === 'no-lifecycle'
          ? ('unlearnable' as const)
          : ('abort' as const),
    );
    // Before the reason: a gesture the user has moved off is answered with
    // nothing whatever the wait said.
    if (!spec.heldStill()) return { kind: 'abort' };
    if (waited === 'abort') return { kind: 'abort' };
    if (waited === 'unlearnable') return { kind: 'unlearnable', target };

    const next = spec.resolve();
    // The learnt policy rules the typed chain out: it names no object in this
    // catalog. Acting on the case-different neighbour the insensitive fallback
    // had picked is the outcome the wait exists to prevent.
    if (!next) return { kind: 'abort' };
    if (next.database === target.database) return { kind: 'resolved', target: next };
    target = next;
  }
  return { kind: 'abort' };
}
